import {BaseComponent} from '../base/component.base.js';
import template from './billing-add-item.template.html';
import styles from './billing-add-item.styles.css';
import {validateBillingItemForm} from '../../core/billing/billing.core.js';
import {confirmDiscard} from '../confirm-dialog/confirm-discard.js';
import {
    billingFormSnapshot,
    connectIndexationCalculator,
    fillBillingForm,
    readBillingForm,
    showBillingFormErrors,
    withBillingItemFields,
} from '../billing-item-form/billing-item-form.view.js';

const CHOOSE_CLIENT = 'Choose a client...';
const CLIENT_REQUIRED = 'Choose the client this item belongs to.';

/**
 * The "Add item" form of the billing overview: the billing card's item form (same fields, defaults
 * and validation) with a client selector in front, since the overview is not tied to one client. It
 * owns the open form and its unsaved-changes state, and only emits events; the driving adapter
 * talks to the API and hands the clients in through `open`.
 *
 * Events (bubbling, composed): `billing-add-save {slug, request}`, and `clipboard-copy {text, label}`
 * from the form's indexation calculator.
 */
export class BillingAddItemComponent extends BaseComponent {
    #open = false;
    #saving = false;
    /** Form values as last opened; see `hasUnsavedChanges`. */
    #savedSnapshot = null;

    /** Asks the browser to confirm closing or reloading the tab while the form has unsaved changes. */
    #onBeforeUnload = (e) => {
        if (this.hasUnsavedChanges()) {
            e.preventDefault();
            e.returnValue = '';
        }
    };

    connectedCallback() {
        const {html, css} = withBillingItemFields(template, styles);
        this.loadTemplate(html, css);
        window.addEventListener('beforeunload', this.#onBeforeUnload);
        this.$('#billing-form').addEventListener('submit', (e) => this.#handleSubmit(e));
        connectIndexationCalculator(this.$('#billing-form'), (name, detail) => this.emit(name, detail));
        this.$('#btn-cancel-billing').addEventListener('click', () => this.discardChangesIfConfirmed());
    }

    disconnectedCallback() {
        window.removeEventListener('beforeunload', this.#onBeforeUnload);
    }

    /** @returns {boolean} whether the form is showing */
    get isOpen() {
        return this.#open;
    }

    /**
     * Shows an empty form (the billing card's defaults) with the clients to choose from.
     *
     * @param {{slug: string, name: string}[]} clients
     */
    open(clients) {
        const form = this.$('#billing-form');
        const select = this.$('#billing-client');
        select.replaceChildren(new Option(CHOOSE_CLIENT, ''), ...clients.map(({slug, name}) => new Option(name, slug)));
        fillBillingForm(form, null);
        this.#showClientError(null);
        this.showFormError(null);
        form.hidden = false;
        this.#open = true;
        this.#savedSnapshot = this.#snapshot();
        select.focus();
    }

    /** Puts the cursor back in the open form, for a second press of the button that opened it. */
    focusForm() {
        this.$('#billing-client').focus();
    }

    /** Closes the form and forgets what was typed in it. */
    closeForm() {
        this.#open = false;
        this.#savedSnapshot = null;
        this.$('#billing-form').hidden = true;
        this.showFormError(null);
    }

    /**
     * Whether a save is running: Save and Cancel are disabled, Save says so, and submits are ignored.
     *
     * @param {boolean} value
     */
    set saving(value) {
        this.#saving = value;
        this.$('#btn-submit-billing').disabled = value;
        this.$('#btn-cancel-billing').disabled = value;
        this.$('#btn-submit-billing span').textContent = value ? 'Saving...' : 'Save item';
        this.$('#billing-form').setAttribute('aria-busy', String(value));
    }

    get saving() {
        return this.#saving;
    }

    /**
     * Shows why the backend rejected the item, in the form where it gets fixed.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} true when the form is open and shows the message
     */
    showFormError(message) {
        const error = this.$('#billing-form-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.#open;
    }

    /** @returns {boolean} true when the open form differs from the empty form it started as */
    hasUnsavedChanges() {
        return this.#open && this.#snapshot() !== this.#savedSnapshot;
    }

    /**
     * Closes the form, first asking whether to drop what was typed when there is any. A form that
     * is saving is left alone (true, no question): the save goes on and reports its own result.
     *
     * @returns {Promise<boolean>} false when the user chose to keep editing; true when the form is closed or was never open
     */
    async discardChangesIfConfirmed() {
        if (this.#saving) {
            return true;
        }
        if (this.hasUnsavedChanges() && !await confirmDiscard(
            'Discard the new billing item?',
            'The billing item you started is not saved and will be lost.',
        )) {
            return false;
        }
        this.closeForm();
        return true;
    }

    #snapshot() {
        return JSON.stringify([this.$('#billing-client').value, billingFormSnapshot(this.$('#billing-form'))]);
    }

    #showClientError(message) {
        const error = this.$('#billing-error-client');
        error.textContent = message || '';
        error.hidden = !message;
        this.$('#billing-client').setAttribute('aria-invalid', String(Boolean(message)));
    }

    #handleSubmit(e) {
        e.preventDefault();
        if (this.#saving) return;
        this.showFormError(null);
        const form = this.$('#billing-form');
        const slug = this.$('#billing-client').value;
        const {valid, errors, request} = validateBillingItemForm(readBillingForm(form));
        showBillingFormErrors(form, errors);
        this.#showClientError(slug ? null : CLIENT_REQUIRED);
        if (!slug) {
            this.focusForm();
        } else if (valid) {
            this.emit('billing-add-save', {slug, request});
        }
    }
}

customElements.define('billing-add-item', BillingAddItemComponent);
